import React, { useState, memo } from 'react';
import { lineStatus } from '../../../utils/enums';
import { useLinesContext } from '../../../contexts/Lines';
import { useEditLineContext } from '../../../contexts/EditLine';
import { Color } from '../../../themes/styles';
import { lineRowHeight, lazyLoadOffset, is8DataApp } from '../../../utils';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Total from './LineTotal';
import Units from './Units';
import Duration from './Duration';
import LineStatus from './LineStatus';
import LineNameRow from './Desktop/LineNameRow';
import DividerVertical from '../Misc/DividerVertical';
import DividerHorizontal from '../Misc/DividerHorizontal';
import LazyLoad from 'react-lazyload';
import LineSummaryPlaceholder from '../Placeholders/LineSummaryPlaceholder';

const useStyles = makeStyles((theme: any) => {
  const dividerMargin = theme.spacing(1.5);
  return {
    lineSummary: {
      height: lineRowHeight,
      backgroundColor: ({ selectedLine }: any) => {
        return selectedLine ? theme.palette.brand.white : '';
      },
      borderRadius: ({ selectedLine }: any) => {
        return selectedLine ? theme.spacing(1) : '';
      },
      marginTop: ({ selectedLine }: any) => {
        return selectedLine ? theme.spacing(1) : '';
      },
      paddingRight: theme.spacing(2),
      paddingLeft: theme.spacing(2),
      boxShadow: ({ selectedLine }: any) => {
        return selectedLine
          ? 'rgb(0 0 0 / 20%) 0px 4px 8px 0px, rgb(0 0 0 / 14%) 0px 4px 8px 0px, rgb(0 0 0 / 12%) 0px 4px 8px 0px'
          : '';
      },
      '& .MuiInputBase-input': {
        fontWeight: 'bold',
        padding: 0,
        paddingBottom: theme.spacing(0.5),
      },
    },
    lineDetails: {
      display: 'flex',
    },
    lineDetailsLeftSide: {
      width: `calc(50% - ${dividerMargin}px)`,
    },
    lineDetailsRightSide: {
      width: `calc(50% - ${dividerMargin}px)`,
    },
    lineDetailsDivider: {
      marginRight: dividerMargin,
      marginLeft: dividerMargin,
    },
  };
});

function compare(prevProps, currentProps) {
  if (prevProps.line.checked !== currentProps.line.checked) {
    return false;
  }
  if (prevProps.line.id !== currentProps.line.id) {
    return false;
  }
  if (prevProps.lineOnEditMode !== currentProps.lineOnEditMode) {
    return false;
  }
  if (prevProps.line.edit || currentProps.line.edit) {
    return false;
  }
  if (
    prevProps.line.editActiveNameLine ||
    currentProps.line.editActiveNameLine
  ) {
    return false;
  }
  return true;
}

function LineSummary({
  line,
  setAnchorOpened,
  handleEditActiveLineDetails,
  setLineInfoDuringEdit,
  addOrDeleteError,
  lineOnEditMode,
}: any) {
  const [selectedLine, setSelectedLine] = useState(false);
  const { lineSummaryRef } = useEditLineContext();
  const { lineOnEditMode: someLineEdited } = useLinesContext();

  const classes = useStyles({
    selectedLine: selectedLine || line.edit || line.editActiveLine,
  });
  const isLineEditable = line.status === lineStatus.draft;
  const is8Data = is8DataApp();

  const {
    editLineClick,
    deleteLineClick,
    changeActiveLineClick,
    editActiveLineNameClick,
  } = useLinesContext();
  const actions = [];
  if (isLineEditable) {
    actions.push({
      name: 'Edit',
      icon: './icons/edit.svg',
      onClick: () => editLineClick(line),
      color: 'black',
    });
  }
  if (
    line.status === lineStatus.active ||
    line.status === lineStatus.complete
  ) {
    actions.push({
      name: 'Request Change',
      icon: './icons/requestChange.svg',
      onClick: () => changeActiveLineClick(line),
      color: 'black',
    });
    actions.push({
      name: 'Edit Line Name',
      icon: './icons/edit.svg',
      onClick: () => editActiveLineNameClick(line),
      color: 'black',
    });
  }
  if (isLineEditable) {
    actions.push({
      name: 'Delete',
      icon: './icons/delete.svg',
      onClick: () => deleteLineClick(line),
      color: 'red',
    });
  }

  return (
    <div className={classes.lineSummary} ref={lineSummaryRef}>
      <DividerHorizontal
        color={selectedLine || line.edit ? Color.WHITE : Color.GREEN_DEEP}
      />
      <LazyLoad
        offset={lazyLoadOffset}
        placeholder={<LineSummaryPlaceholder />}
      >
        <>
          <LineNameRow
            line={line}
            actions={actions}
            setAnchorOpened={setAnchorOpened}
            setSelectedLine={setSelectedLine}
            handleEditActiveLineDetails={handleEditActiveLineDetails}
            setLineInfoDuringEdit={setLineInfoDuringEdit}
            addOrDeleteError={addOrDeleteError}
            lineOnEditMode={lineOnEditMode}
          />
          <div
            className={classes.lineDetails}
            onClick={() => !someLineEdited && editLineClick(line)}
          >
            <div className={classes.lineDetailsLeftSide}>
              <Total line={line} />
              {!is8Data && <LineStatus line={line} />}
            </div>
            <div className={classes.lineDetailsDivider}>
              <DividerVertical />
            </div>
            <div className={classes.lineDetailsRightSide}>
              <Units
                line={line}
                setLineInfoDuringEdit={setLineInfoDuringEdit}
                addOrDeleteError={addOrDeleteError}
              />
              <Duration line={line} />
            </div>
          </div>
        </>
      </LazyLoad>
    </div>
  );
}

function LineSummaryOnEdit({
  line,
  setAnchorOpened,
  handleEditActiveLineDetails,
  setLineInfoDuringEdit,
  addOrDeleteError,
  lineOnEditMode,
}: any) {
  const { line: EditLine } = useEditLineContext();
  let lineToUse = line;
  if (EditLine?.id === line.id) {
    lineToUse = {
      ...EditLine,
      edit: line.edit,
      editActiveLine: line.editActiveLine,
    };
  }
  return (
    <LineSummary
      line={lineToUse}
      setAnchorOpened={setAnchorOpened}
      handleEditActiveLineDetails={handleEditActiveLineDetails}
      setLineInfoDuringEdit={setLineInfoDuringEdit}
      addOrDeleteError={addOrDeleteError}
      lineOnEditMode={lineOnEditMode}
    />
  );
}

export default memo(function ({
  line,
  setAnchorOpened,
  handleEditActiveLineDetails,
  setLineInfoDuringEdit,
  addOrDeleteError,
  lineOnEditMode,
}: any) {
  const LineSummaryComponent =
    line.edit || line.editActiveLine ? LineSummaryOnEdit : LineSummary;
  return (
    <LineSummaryComponent
      line={line}
      setAnchorOpened={setAnchorOpened}
      handleEditActiveLineDetails={handleEditActiveLineDetails}
      setLineInfoDuringEdit={setLineInfoDuringEdit}
      addOrDeleteError={addOrDeleteError}
      lineOnEditMode={lineOnEditMode}
    />
  );
},
compare);
